import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import { Modal } from "../../../components/Modal";
import { DetailsToggle } from "../../../components/DetailsToggle";
import { ListToolbar } from "../../../components/ListToolbar";
import {
  ApiError,
  approveMeasurementAuto,
  createMeasurementAuto,
  deleteMeasurementAuto,
  exportMeasurementAutoToPdf,
  generateInvoiceFromAuto,
  getMeasurementAutos,
  reopenMeasurementAuto,
  updateMeasurementAuto,
  type MeasurementAutoDto,
  type MeasurementAutoPayload,
  type MeasurementAutoStatus,
  type MeasurementAutoType,
} from "../../../obras/obrasApi";

interface WorkMeasurementAutosTabProps {
  workId: number;
}

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: APP_CURRENCY,
});

const formatCurrency = (value: number): string => {
  if (!Number.isFinite(value)) return currencyFormatter.format(0);
  return currencyFormatter.format(value);
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
};

const todayInput = (): string => new Date().toISOString().slice(0, 10);

// Tolerant number parse (accepts comma or period), like WorkFinancialDocumentsTab.
const parseNumber = (value: string): number => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const typeLabel = (type: MeasurementAutoType): string =>
  type === "final" ? "Final" : "Intermédio";

const statusLabel = (status: MeasurementAutoStatus): string =>
  status === "issued" ? "Emitido" : status === "approved" ? "Aprovado" : "Rascunho";

// Badge de estado a 3 cores. Verde (aprovado) usa border-border porque
// border-green-500/20 não existe no Tailwind pré-compilado.
const statusBadgeClass = (status: MeasurementAutoStatus): string => {
  if (status === "issued") return "bg-primary/10 text-primary border border-primary/20";
  if (status === "approved") return "bg-green-500/10 text-green-700 border border-border";
  return "bg-muted text-secondary-text border border-border";
};

// Ações de lifecycle por-linha (aprovar / reabrir / gerar fatura).
type AutoAction = "approve" | "reopen" | "invoice";

// Editable line shape for the in-modal editor (strings so the inputs stay controlled).
interface LineForm {
  key: string;
  description: string;
  unit: string;
  quantity: string;
  unitPrice: string;
}

interface AutoForm {
  autoType: MeasurementAutoType;
  autoDate: string;
  periodStart: string;
  periodEnd: string;
  number: string;
  clientName: string;
  status: MeasurementAutoStatus;
  notes: string;
  lines: LineForm[];
}

let lineKeySeq = 0;
const nextLineKey = (): string => {
  lineKeySeq += 1;
  return `line-${lineKeySeq}`;
};

const emptyLine = (): LineForm => ({
  key: nextLineKey(),
  description: "",
  unit: "",
  quantity: "",
  unitPrice: "",
});

const emptyForm = (autoType: MeasurementAutoType): AutoForm => ({
  autoType,
  autoDate: todayInput(),
  periodStart: "",
  periodEnd: "",
  number: "",
  clientName: "",
  status: "draft",
  notes: "",
  lines: [emptyLine()],
});

const formFromAuto = (auto: MeasurementAutoDto): AutoForm => ({
  autoType: auto.autoType,
  autoDate: toInputDate(auto.autoDate),
  periodStart: toInputDate(auto.periodStart),
  periodEnd: toInputDate(auto.periodEnd),
  number: auto.number ?? "",
  clientName: auto.clientName ?? "",
  status: auto.status,
  notes: auto.notes ?? "",
  lines:
    auto.lines.length > 0
      ? auto.lines
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((line) => ({
            key: nextLineKey(),
            description: line.description ?? "",
            unit: line.unit ?? "",
            quantity: line.quantity !== null && line.quantity !== undefined ? String(line.quantity) : "",
            unitPrice: line.unitPrice !== null && line.unitPrice !== undefined ? String(line.unitPrice) : "",
          }))
      : [emptyLine()],
});

const lineAmount = (line: LineForm): number =>
  parseNumber(line.quantity) * parseNumber(line.unitPrice);

const formTotal = (form: AutoForm): number =>
  form.lines.reduce((sum, line) => sum + lineAmount(line), 0);

const buildPayload = (form: AutoForm): MeasurementAutoPayload => ({
  autoType: form.autoType,
  autoDate: form.autoDate,
  periodStart: form.periodStart || null,
  periodEnd: form.periodEnd || null,
  number: form.number.trim() || null,
  clientName: form.clientName.trim() || null,
  status: form.status,
  notes: form.notes.trim() || null,
  lines: form.lines
    .filter((line) => line.description.trim() !== "" || line.quantity.trim() !== "" || line.unitPrice.trim() !== "")
    .map((line, index) => ({
      description: line.description.trim(),
      unit: line.unit.trim() || null,
      quantity: parseNumber(line.quantity),
      unitPrice: parseNumber(line.unitPrice),
      sortOrder: index,
    })),
});

export function WorkMeasurementAutosTab({ workId }: WorkMeasurementAutosTabProps) {
  const navigate = useNavigate();
  const [autos, setAutos] = useState<MeasurementAutoDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [actioning, setActioning] = useState<{ id: number; kind: AutoAction } | null>(null);
  const [invoiceMessage, setInvoiceMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const [editingAuto, setEditingAuto] = useState<MeasurementAutoDto | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<AutoForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpanded = (autoId: number) =>
    setExpandedId((prev) => (prev === autoId ? null : autoId));

  const loadAutos = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setAutos(await getMeasurementAutos(workId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os autos de medição.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    loadAutos();
  }, [loadAutos]);

  // Client-side filter: case-insensitive substring across the listed text columns.
  const normalizedFilter = filter.trim().toLowerCase();
  const shown = useMemo(() => {
    if (normalizedFilter === "") return autos;
    return autos.filter((auto) =>
      [auto.number, typeLabel(auto.autoType), statusLabel(auto.status)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedFilter)),
    );
  }, [autos, normalizedFilter]);

  // Total of the currently visible (filtered) rows, for the compact toolbar summary.
  const total = useMemo(
    () => shown.reduce((sum, auto) => sum + (Number.isFinite(auto.totalAmount) ? auto.totalAmount : 0), 0),
    [shown],
  );

  const openCreate = (autoType: MeasurementAutoType) => {
    setEditingAuto(null);
    setIsCreating(true);
    setForm(emptyForm(autoType));
  };

  const openEdit = (auto: MeasurementAutoDto) => {
    setIsCreating(false);
    setEditingAuto(auto);
    setForm(formFromAuto(auto));
  };

  const closeModal = () => {
    setEditingAuto(null);
    setIsCreating(false);
    setForm(null);
  };

  const updateForm = (patch: Partial<AutoForm>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const updateLine = (key: string, patch: Partial<LineForm>) =>
    setForm((prev) =>
      prev
        ? { ...prev, lines: prev.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)) }
        : prev,
    );

  const addLine = () =>
    setForm((prev) => (prev ? { ...prev, lines: [...prev.lines, emptyLine()] } : prev));

  const removeLine = (key: string) =>
    setForm((prev) =>
      prev
        ? {
            ...prev,
            lines: prev.lines.length > 1 ? prev.lines.filter((line) => line.key !== key) : prev.lines,
          }
        : prev,
    );

  const handleSave = async () => {
    if (!form || isSaving) return;
    if (!form.autoDate) {
      setError("A data do auto é obrigatória.");
      return;
    }
    const payload = buildPayload(form);
    if (payload.lines.length === 0) {
      setError("Adicione pelo menos uma linha ao auto.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (editingAuto) {
        await updateMeasurementAuto(workId, editingAuto.id, payload);
      } else {
        await createMeasurementAuto(workId, payload);
      }
      closeModal();
      await loadAutos(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível guardar o auto.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (autoId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este auto de medição?")) return;
    setDeletingId(autoId);
    setError(null);
    try {
      await deleteMeasurementAuto(workId, autoId);
      setAutos((prev) => prev.filter((item) => item.id !== autoId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setAutos((prev) => prev.filter((item) => item.id !== autoId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o auto.");
        loadAutos(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async (autoId: number) => {
    if (exportingId !== null) return;
    setExportingId(autoId);
    setError(null);
    try {
      await exportMeasurementAutoToPdf(workId, autoId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível exportar o PDF.");
    } finally {
      setExportingId(null);
    }
  };

  // Aprovar / reabrir / gerar fatura — ações reversíveis/idempotentes (sem window.confirm).
  const runAction = async (autoId: number, kind: AutoAction) => {
    if (actioning !== null) return;
    setActioning({ id: autoId, kind });
    setError(null);
    if (kind === "invoice") setInvoiceMessage(null);
    try {
      if (kind === "approve") {
        await approveMeasurementAuto(workId, autoId);
        await loadAutos(true);
      } else if (kind === "reopen") {
        await reopenMeasurementAuto(workId, autoId);
        await loadAutos(true);
      } else {
        const invoice = await generateInvoiceFromAuto(workId, autoId);
        const label = invoice.number ? `fatura ${invoice.number}` : "fatura";
        setInvoiceMessage(`Rascunho de ${label} criado em Contas a Receber.`);
        await loadAutos(true);
      }
    } catch (err) {
      const fallback =
        kind === "approve"
          ? "Não foi possível aprovar o auto."
          : kind === "reopen"
            ? "Não foi possível reabrir o auto."
            : "Não foi possível gerar a fatura.";
      setError(err instanceof Error ? err.message : fallback);
    } finally {
      setActioning(null);
    }
  };

  // Abre a fatura gerada em Contas a Receber (deep-link ?faturaNumero=<n>).
  const goToInvoice = (invoiceNumber?: string | null) => {
    if (!invoiceNumber) return;
    navigate(`/contas-a-receber?faturaNumero=${encodeURIComponent(invoiceNumber)}`);
  };

  const inputClass = "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";
  const linkAction =
    "underline cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {invoiceMessage && (
        <div
          className="border border-border bg-green-500/10 text-green-700 px-4 py-3 flex items-start justify-between gap-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          <span>{invoiceMessage}</span>
          <button
            type="button"
            onClick={() => setInvoiceMessage(null)}
            className={`${linkAction} text-green-700`}
          >
            Fechar
          </button>
        </div>
      )}

      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar autos…"
        summary={
          <span className="text-secondary-text text-sm">
            {shown.length} auto(s) · Total {formatCurrency(total)}
          </span>
        }
      >
        <button
          type="button"
          onClick={() => openCreate("intermediate")}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Auto intermédio
        </button>
        <button
          type="button"
          onClick={() => openCreate("final")}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-secondary hover:bg-accent border border-border rounded transition-colors cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Auto final de entrega
        </button>
      </ListToolbar>

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="w-full" style={{ minWidth: 960 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 56 }}></th>
              <th className="px-4 py-3" style={{ width: 140 }}>Nº</th>
              <th className="px-4 py-3" style={{ width: 120 }}>Tipo</th>
              <th className="px-4 py-3" style={{ width: 120 }}>Data</th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>Total</th>
              <th className="px-4 py-3" style={{ width: 110 }}>Estado</th>
              <th className="px-4 py-3" style={{ width: 300 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-text">{filter !== "" ? "Sem resultados para o filtro." : "Sem autos de medição. Crie o primeiro auto acima."}</td></tr>
            ) : shown.map((auto) => (
              <Fragment key={auto.id}>
                <tr className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3">
                    <DetailsToggle
                      open={expandedId === auto.id}
                      onToggle={() => toggleExpanded(auto.id)}
                      showLabel="Ver linhas"
                      hideLabel="Ocultar linhas"
                    />
                  </td>
                  <td className="px-4 py-3 break-words">{auto.number || "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {typeLabel(auto.autoType)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(auto.autoDate)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(auto.totalAmount)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-xs ${statusBadgeClass(auto.status)}`}
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {statusLabel(auto.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 flex-wrap items-center">
                      {auto.status === "draft" && (
                        <button
                          className={`${linkAction} text-primary`}
                          type="button"
                          disabled={actioning?.id === auto.id}
                          onClick={() => runAction(auto.id, "approve")}
                        >
                          {actioning?.id === auto.id && actioning.kind === "approve" ? "A aprovar…" : "Aprovar"}
                        </button>
                      )}
                      {/* Reabrir só faz sentido em auto aprovado e ainda não faturado. */}
                      {auto.status === "approved" && !auto.invoiceNumber && (
                        <button
                          className={`${linkAction} text-primary`}
                          type="button"
                          disabled={actioning?.id === auto.id}
                          onClick={() => runAction(auto.id, "reopen")}
                        >
                          {actioning?.id === auto.id && actioning.kind === "reopen" ? "A reabrir…" : "Reabrir"}
                        </button>
                      )}
                      {/* Gerar fatura só enquanto não houver fatura viva ligada. */}
                      {!auto.invoiceNumber && (auto.status === "approved" || auto.status === "issued") && (
                        <button
                          className={`${linkAction} text-primary`}
                          type="button"
                          disabled={actioning?.id === auto.id}
                          onClick={() => runAction(auto.id, "invoice")}
                        >
                          {actioning?.id === auto.id && actioning.kind === "invoice" ? "A gerar…" : "Gerar fatura"}
                        </button>
                      )}
                      {/* Auto já faturado: mostra a fatura e liga a Contas a Receber. */}
                      {auto.invoiceNumber && (
                        <button
                          className={`${linkAction} text-green-700`}
                          type="button"
                          title="Abrir a fatura em Contas a Receber"
                          onClick={() => goToInvoice(auto.invoiceNumber)}
                        >
                          Ver fatura {auto.invoiceNumber}
                        </button>
                      )}
                      {/* Editar/Eliminar só em rascunho (congelados após aprovar). */}
                      {auto.status === "draft" && (
                        <button
                          className={`${linkAction} text-primary`}
                          type="button"
                          onClick={() => openEdit(auto)}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        className={`${linkAction} text-primary`}
                        type="button"
                        disabled={exportingId === auto.id}
                        onClick={() => handleExport(auto.id)}
                      >
                        {exportingId === auto.id ? "A exportar…" : "Exportar PDF"}
                      </button>
                      {auto.status === "draft" && (
                        <button
                          className={`${linkAction} text-destructive`}
                          type="button"
                          disabled={deletingId === auto.id}
                          onClick={() => handleDelete(auto.id)}
                        >
                          {deletingId === auto.id ? "A eliminar…" : "Eliminar"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedId === auto.id && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={7} className="px-4 py-3 bg-muted">
                      {auto.lines.length === 0 ? (
                        <div className="text-sm text-secondary-text">Sem linhas neste auto.</div>
                      ) : (
                        <table className="w-full" style={{ minWidth: 520 }}>
                          <thead>
                            <tr className="text-left text-secondary-text text-xs">
                              <th className="px-2 py-1">Descrição</th>
                              <th className="px-2 py-1" style={{ width: 70 }}>Un.</th>
                              <th className="px-2 py-1" style={{ width: 80 }}>Qtd</th>
                              <th className="px-2 py-1" style={{ width: 110 }}>Preço unit.</th>
                              <th className="px-2 py-1 text-right" style={{ width: 110 }}>Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {auto.lines
                              .slice()
                              .sort((a, b) => a.sortOrder - b.sortOrder)
                              .map((line) => (
                                <tr key={line.id} className="border-t border-border">
                                  <td className="px-2 py-1 break-words">{line.description || "—"}</td>
                                  <td className="px-2 py-1">{line.unit || "—"}</td>
                                  <td className="px-2 py-1">{line.quantity}</td>
                                  <td className="px-2 py-1">{formatCurrency(line.unitPrice)}</td>
                                  <td className="px-2 py-1 text-right whitespace-nowrap">
                                    {formatCurrency(line.quantity * line.unitPrice)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      )}
                      {(auto.periodStart || auto.periodEnd) && (
                        <div className="text-sm text-secondary-text mt-2">
                          Período: {formatDate(auto.periodStart)} – {formatDate(auto.periodEnd)}
                        </div>
                      )}
                      {(auto.clientName || auto.notes) && (
                        <div className="text-sm text-secondary-text mt-2">
                          {auto.clientName ? `Cliente: ${auto.clientName}` : ""}
                          {auto.clientName && auto.notes ? " · " : ""}
                          {auto.notes ? `Notas: ${auto.notes}` : ""}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {form && (isCreating || editingAuto) && (
        <Modal
          title={editingAuto ? "Editar auto de medição" : `Novo auto ${typeLabel(form.autoType).toLowerCase()}`}
          onClose={closeModal}
          maxWidthClass="max-w-4xl"
          footer={
            <>
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-border">Cancelar</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "A guardar…" : "Guardar"}
              </button>
            </>
          }
        >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Tipo *
                <select
                  value={form.autoType}
                  onChange={(event) => updateForm({ autoType: event.target.value as MeasurementAutoType })}
                  className={inputClass}
                >
                  <option value="intermediate">Intermédio</option>
                  <option value="final">Final de entrega</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Data *
                <input
                  type="date"
                  value={form.autoDate}
                  onChange={(event) => updateForm({ autoDate: event.target.value })}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Início do período
                <input
                  type="date"
                  value={form.periodStart}
                  onChange={(event) => updateForm({ periodStart: event.target.value })}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Fim do período
                <input
                  type="date"
                  value={form.periodEnd}
                  onChange={(event) => updateForm({ periodEnd: event.target.value })}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Estado
                <select
                  value={form.status}
                  onChange={(event) => updateForm({ status: event.target.value as MeasurementAutoStatus })}
                  className={inputClass}
                >
                  <option value="draft">Rascunho</option>
                  <option value="issued">Emitido</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Nº
                <input
                  type="text"
                  value={form.number}
                  onChange={(event) => updateForm({ number: event.target.value })}
                  placeholder="Nº do auto (opcional)"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Cliente
                <input
                  type="text"
                  value={form.clientName}
                  onChange={(event) => updateForm({ clientName: event.target.value })}
                  placeholder="Cliente (opcional)"
                  className={inputClass}
                />
              </label>
              <label
                className="flex flex-col gap-1 text-sm text-secondary-text"
                style={{ gridColumn: "1 / -1" }}
              >
                Notas
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(event) => updateForm({ notes: event.target.value })}
                  className={inputClass}
                />
              </label>
            </div>

            <div className="border border-border overflow-x-auto" style={{ borderRadius: "var(--radius)" }}>
              <table className="w-full" style={{ minWidth: 720 }}>
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className="px-3 py-2">Descrição</th>
                    <th className="px-3 py-2" style={{ width: 90 }}>Unid.</th>
                    <th className="px-3 py-2" style={{ width: 110 }}>Qtd</th>
                    <th className="px-3 py-2" style={{ width: 130 }}>Preço unit.</th>
                    <th className="px-3 py-2 text-right" style={{ width: 130 }}>Valor</th>
                    <th className="px-3 py-2" style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line) => (
                    <tr key={line.key} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={line.description}
                          onChange={(event) => updateLine(line.key, { description: event.target.value })}
                          placeholder="Descrição"
                          className={`${inputClass} w-full`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={line.unit}
                          onChange={(event) => updateLine(line.key, { unit: event.target.value })}
                          placeholder="un"
                          className={`${inputClass} w-full`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={line.quantity}
                          onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
                          placeholder="0"
                          className={`${inputClass} w-full`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={line.unitPrice}
                          onChange={(event) => updateLine(line.key, { unitPrice: event.target.value })}
                          placeholder="0,00"
                          className={`${inputClass} w-full`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{formatCurrency(lineAmount(line))}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeLine(line.key)}
                          disabled={form.lines.length <= 1}
                          className={`${linkAction} text-destructive`}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted">
                    <td className="px-3 py-2 text-secondary-text text-sm" colSpan={4}>Total</td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{formatCurrency(formTotal(form))}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div>
              <button
                type="button"
                onClick={addLine}
                className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors text-sm"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                + Adicionar linha
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}
